import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContexte';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAccess } from '../../contexts/AccessContext';
import LanguageSwitch from '../layout/LanguageSwitch';
import Logo from '../ui/Logo';
import Icon from '../ui/Icon';
import ServerIcon from '../ui/ServerIcon';
import './WelcomePage.css';

const favoritesKey = (userId) => `WELCOME_FAVORITES_${userId}`;

const loadFavorites = (userId) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(favoritesKey(userId)) || '[]');
        return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
        return new Set();
    }
};

const WelcomePage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { token, user, loading, logout } = useAuth();
    const { can } = useAccess();
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (loading) return;
        if (!token || !user) navigate('/');
    }, [token, user, loading, navigate]);

    useEffect(() => {
        if (user?.id) setFavoriteIds(loadFavorites(user.id));
    }, [user?.id]);

    const toggleFavorite = (guildId) => {
        setFavoriteIds((current) => {
            const next = new Set(current);
            if (next.has(guildId)) next.delete(guildId);
            else next.add(guildId);
            if (user?.id) localStorage.setItem(favoritesKey(user.id), JSON.stringify([...next]));
            return next;
        });
    };

    // Serveurs admin + membre sans doublon ; tri : favoris, puis admin, puis nom
    const adminIds = useMemo(() => {
        const all = [...(user?.adminGuilds || []), ...(user?.memberGuilds || [])];
        return new Set(all.filter(g => can(g.id)).map(g => g.id));
    }, [user, can]);
    const guilds = useMemo(() => {
        const byId = new Map();
        (user?.adminGuilds || []).forEach(g => byId.set(g.id, g));
        (user?.memberGuilds || []).forEach(g => { if (!byId.has(g.id)) byId.set(g.id, g); });
        return [...byId.values()].sort((a, b) =>
            (favoriteIds.has(b.id) - favoriteIds.has(a.id))
            || (adminIds.has(b.id) - adminIds.has(a.id))
            || a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
        );
    }, [user, adminIds, favoriteIds]);

    const visibleGuilds = guilds.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    if (loading || !user) {
        return (
            <main className="c2-page welcome-loading">
                <div className="c2-spinner" />
                <p className="c2-muted">{t('common.loading')}</p>
            </main>
        );
    }

    return (
        <main className="c2-page welcome-page">
            <header className="welcome-topbar">
                <div className="welcome-topbar__brand">
                    <Logo size={52} />
                    <span className="welcome-topbar__name">LadderPVP</span>
                </div>
                <div className="welcome-topbar__actions">
                    <div className="welcome-user">
                        {user.avatar && (
                            <img
                                className="welcome-user__avatar"
                                src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`}
                                alt=""
                            />
                        )}
                        <span className="welcome-user__name">{user.username}</span>
                    </div>
                    <LanguageSwitch variant="c2" />
                    <button type="button" className="c2-icon-btn" onClick={handleLogout} aria-label={t('common.logout')} title={t('common.logout')}>
                        <Icon name="logout" size={20} />
                    </button>
                </div>
            </header>

            <div className="welcome-content">
                <div className="welcome-hero c2-fade-up">
                    <h1>{t('welcome.hello', 'Bonjour, {name}').replace('{name}', user.username)}</h1>
                    <p className="c2-muted">{t('welcome.subtitle')}</p>
                </div>

                {guilds.length > 3 && (
                    <label className="welcome-search">
                        <Icon name="search" size={18} />
                        <span className="visually-hidden">{t('welcome.searchPlaceholder', 'Rechercher un serveur')}</span>
                        <input
                            type="search"
                            className="c2-input"
                            placeholder={t('welcome.searchPlaceholder', 'Rechercher un serveur')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </label>
                )}

                {visibleGuilds.length === 0 ? (
                    <div className="c2-card welcome-empty">
                        <span className="c2-diamond c2-diamond--purple welcome-empty__mark" />
                        <h2>{t('welcome.noServers')}</h2>
                        <p className="c2-muted">
                            {searchTerm
                                ? t('welcome.noMatch', 'Aucun serveur ne correspond à « {term} »').replace('{term}', searchTerm)
                                : t('welcome.noAccess', "Le bot n'est sur aucun de tes serveurs Discord pour le moment.")}
                        </p>
                    </div>
                ) : (
                    <ul className="server-grid">
                        {visibleGuilds.map((guild, index) => {
                            const isAdmin = adminIds.has(guild.id);
                            const isFavorite = favoriteIds.has(guild.id);
                            return (
                                <li key={guild.id} className="c2-card server-card c2-fade-up" style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}>
                                    <button
                                        type="button"
                                        className={`server-card__fav${isFavorite ? ' is-active' : ''}`}
                                        onClick={() => toggleFavorite(guild.id)}
                                        aria-pressed={isFavorite}
                                        aria-label={`${isFavorite ? t('welcome.favoriteRemove', 'Retirer des favoris') : t('welcome.favoriteAdd', 'Ajouter aux favoris')} : ${guild.name}`}
                                        title={isFavorite ? t('welcome.favoriteRemove', 'Retirer des favoris') : t('welcome.favoriteAdd', 'Ajouter aux favoris')}
                                    >
                                        <Icon name="star" size={18} filled={isFavorite} />
                                    </button>

                                    <div className="server-card__head">
                                        <ServerIcon guild={guild} size={56} />
                                        <div className="server-card__info">
                                            <h2 className="server-card__name">{guild.name}</h2>
                                            {isAdmin ? (
                                                <span className="c2-badge c2-badge--green"><span className="c2-diamond" />{t('welcome.roleAdmin', 'Admin')}</span>
                                            ) : (
                                                <span className="c2-badge c2-badge--purple"><span className="c2-diamond c2-diamond--purple" />{t('welcome.roleMember', 'Membre')}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="server-card__actions">
                                        {isAdmin ? (
                                            <>
                                                <button type="button" className="c2-btn c2-btn--primary" onClick={() => navigate(`/server/${guild.id}`)}>
                                                    <Icon name="shield" size={18} />
                                                    {t('welcome.openAdmin', 'Administrer')}
                                                </button>
                                                <button type="button" className="c2-btn c2-btn--ghost" onClick={() => navigate(`/server/${guild.id}/member`)}>
                                                    {t('welcome.memberAccess')}
                                                </button>
                                            </>
                                        ) : (
                                            <button type="button" className="c2-btn c2-btn--accent" onClick={() => navigate(`/server/${guild.id}/member`)}>
                                                <Icon name="user" size={18} />
                                                {t('welcome.memberAccess')}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </main>
    );
};

export default WelcomePage;
